import React from 'react';
import { useUserSettingsProfileDefaultDataQuery } from '../../../../graphql/graphql';
import UserSettingsProfileForm, {
	UserSettingsProfileFormValues,
} from './UserSettingsProfileForm';
import useApiClient from '../../../Base/Hooks/useApiClient';
import apiClient from '../../../../Services/ApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import useLoading from '../../../Base/Hooks/useLoading';
import { getBase64AsFile } from '../../../Base/Helpers/image.helper';
import LocalStorage from '../../../../Services/LocalStorage';
import { LocalStorageKey } from '../../../Base/Enums/LocalStorageKey.enum';

interface Props {
	userId: string;
	onSuccess: () => void;
}

const UserSettingsProfile: React.FC<Props> = ({ userId, onSuccess }) => {
	const { isLoading, setIsLoading, RenderLoading } = useLoading();
	const { displayError, showUnexpectedError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const {
		data,
		isLoading: isLoadingDefaultData,
		refetch,
	} = useUserSettingsProfileDefaultDataQuery({
		id: userId,
	});

	const uploadAvatar = async (avatar: string): Promise<boolean> => {
		try {
			const formData = new FormData();
			const file = await getBase64AsFile(avatar);
			formData.append('file', file);
			const response = await apiClient.postMultipart(
				`/doctors/${userId}/currentPicture`,
				formData,
			);
			return response.status === 200;
		} catch (e) {
			displayError(e);
		}
		return false;
	};

	const handleSubmit = async (values: UserSettingsProfileFormValues) => {
		setIsLoading(true);
		try {
			if (values.avatar) {
				await uploadAvatar(values.avatar);
			}
			const result = await apiClient.patch(`/doctors/${userId}`, {
				salutationBefore: values.salutationBefore,
				salutationAfter: values.salutationAfter,
				phoneNumber: values.phone,
			});
			if (result.status === 200) {
				showNotificationSuccess({
					content: 'base.profileDataHasBeenSaved',
				});
				// OPEN AND SET TO TRUE TO FORCE MODAL AUTOMATIC OPEN AFTER WINDOW RELOAD
				LocalStorage.set(LocalStorageKey.OPEN_USER_SETTINGS, true);
				window.location.reload();
				onSuccess();
			} else {
				showUnexpectedError();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	if (isLoading || isLoadingDefaultData) {
		return <RenderLoading />;
	}

	return (
		<UserSettingsProfileForm
			onSubmit={handleSubmit}
			defaultValues={{
				salutationBefore: data?.user?.profile?.salutationBefore,
				salutationAfter: data?.user?.profile?.salutationAfter,
				firstName: data?.user?.profile?.firstName,
				lastName: data?.user?.profile?.lastName,
				email: data?.user?.email,
				phone: data?.user?.phoneNumber,
				avatar: data?.user?.avatarUrl,
			}}
		/>
	);
};

export default UserSettingsProfile;
